import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output
} from '@angular/core';
import { List as ImmutableList } from 'immutable';
import { RoleType } from '../../constants-types';
import {
    Group,
    GroupValidateResponse,
    IconSize,
    IconType,
    SharedGroupWithUsers,
    User
} from '../../core-data-model';
import {
    ContextDataService,
    GroupsDataService,
    GroupTransformers,
    notification
} from '../../core-services';
import { Util } from '../../core-utilities';
import {
    InitDataService,
    UserProfileSettingsService
} from '../../system-services';
import { TranslationService } from '../../translations';

@Component({
    selector: 'group-share',
    template: `
        <div class="form formInPeekPanel">
            <div class="title-dashboard" style="margin-bottom: 35px">
                {{ TR.key('sharedTeams.shareThisTeam') }}
            </div>
            <div class="form-group">
                <label class="control-label">
                    <span>{{
                        TR.key('sharedgroups.adduserstogrouplabel')
                    }}</span>
                    @if (sharedGroup) {
                        <gated-pillbox-with-typeahead
                            [(ngModel)]="directPick"
                            [placeholder]="
                                TR.key(
                                    'useradministration.searchbar-placeholder'
                                )
                            "
                            (userAdded)="directShare($event)"
                            (userRemoved)="unshareGroupFromUser($event)"
                            [sharedUsers]="sharedGroup.directUsers"
                        >
                        </gated-pillbox-with-typeahead>
                    }
                </label>
            </div>
            @if (isDirectUserOrSuperAdmin()) {
                <div class="form-group">
                    <label class="control-label">
                        <span>{{
                            TR.key('sharedgroups.addThirdPartyUsers')
                        }}</span>
                        @if (sharedGroup) {
                            <gated-pillbox-with-typeahead
                                [(ngModel)]="thirdPartyPick"
                                [placeholder]="
                                    TR.key(
                                        'useradministration.searchbar-placeholder'
                                    )
                                "
                                [areThirdPartyUsers]="true"
                                (userAdded)="thirdPartyShare($event)"
                                (userRemoved)="unshareGroupFromUser($event)"
                                [sharedUsers]="sharedGroup.thirdPartyUsers"
                            >
                            </gated-pillbox-with-typeahead>
                        }
                    </label>
                </div>
            }
            <button
                class="button button-tertiary button-md"
                (click)="close.emit()"
            >
                {{ TR.key('text.common-terms.close') }}
            </button>
            @if (groupValidationResponse) {
                <shared-groups-permission-modal
                    [validateResponse]="groupValidationResponse"
                    (closeModal)="modalClose()"
                    [isThirdPartyUser]="!isDirectUserOrSuperAdmin()"
                    (addUser)="shareGroupToUserAfterValidation($event)"
                    (removeUser)="removeUserFromPillbox($event)"
                >
                </shared-groups-permission-modal>
            }
        </div>
    `,
    standalone: false
})
export class GroupShareComponent implements OnChanges {
    @Input()
    myGroup: Group;
    @Input()
    isCreatingGroup = false;

    @Output()
    close = new EventEmitter();

    sharedGroup: SharedGroupWithUsers;

    directPick = ImmutableList();
    thirdPartyPick = ImmutableList();

    IconType = IconType;
    IconSize = IconSize;

    groupValidationResponse: GroupValidateResponse;

    // Only direct users and super admins can share groups with third party users and can grant folder permissions to users.
    isDirectUserOrSuperAdmin() {
        return (
            this.userProfileService.isUserLoggedInDefaultCompany() ||
            this.init.hasPermissionLevelOrAbove(RoleType.SuperAdmin)
        );
    }

    constructor(
        public TR: TranslationService,
        public util: Util,
        public context: ContextDataService,
        public groups: GroupsDataService,
        private init: InitDataService,
        private userProfileService: UserProfileSettingsService,
        private transform: GroupTransformers
    ) {}

    ngOnChanges() {
        if (this.myGroup) {
            this.getSharedUsers();
        }
    }

    async getSharedUsers() {
        const allUsers = await this.groups.getUsersWithGroupAccess(
            this.myGroup.id
        );
        this.sharedGroup = {
            group: this.myGroup,
            directUsers: ImmutableList(
                allUsers.filter((user) => !user.isThirdPartyUser)
            ),
            thirdPartyUsers: ImmutableList(
                allUsers.filter((user) => user.isThirdPartyUser)
            )
        };
    }

    async directShare(user: User) {
        await this.validateUserAndShareGroup(user);
        this.directPick = ImmutableList();
    }

    async thirdPartyShare(user: User) {
        await this.validateUserAndShareGroup(user);
        this.thirdPartyPick = ImmutableList();
    }

    async validateUserAndShareGroup(user: User) {
        const validate = await this.groups.validateGroups(
            user.id,
            this.sharedGroup.group.id
        );

        if (validate.length > 0) {
            this.groupValidationResponse = validate[0];
        } else {
            await this.shareGroupToUserNoFolders(
                this.sharedGroup.group.id,
                user.id
            );
        }
    }

    @notification('sharedgroups.success-title', 'sharedTeams.success-message')
    async shareGroupToUserNoFolders(groupId: string, userId: string) {
        await this.groups.giveUsersAccessToGroup({
            sharedGroupsUsersFoldersParameters: [
                {
                    userCompanyMyGroupGuid: groupId,
                    userGuid: userId
                }
            ]
        });
        await this.getSharedUsers();
    }

    @notification('sharedgroups.success-title', 'sharedTeams.success-message')
    async shareGroupToUserAfterValidation(validation: GroupValidateResponse) {
        const shareGroupData =
            this.transform.transformUserAccessClientToServer(validation);

        await this.groups.giveUsersAccessToGroup(shareGroupData);
    }

    @notification(
        'sharedgroups.success-title',
        'sharedTeams.remove-success-message'
    )
    async unshareGroupFromUser(user: User) {
        const unshareGroupData =
            this.transform.transformUnshareUserClientToServer(
                user,
                this.sharedGroup.group.id
            );

        await this.groups.revokeUsersAccessToGroup(unshareGroupData);
    }

    removeUserFromPillbox(userId: string) {
        let index = this.sharedGroup.directUsers.findIndex(
            (u) => u.id === userId
        );

        if (index !== -1) {
            this.sharedGroup.directUsers =
                this.sharedGroup.directUsers.delete(index);
        } else {
            index = this.sharedGroup.thirdPartyUsers.findIndex(
                (u) => u.id === userId
            );

            this.sharedGroup.thirdPartyUsers =
                this.sharedGroup.thirdPartyUsers.delete(index);
        }
    }

    modalClose() {
        this.groupValidationResponse = null;
    }
}
